import { Button } from "@/components/ui/button";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useBuySeats, useSeatQuote } from "@/hooks";
import { cycleNoun, formatMoney } from "@/lib/money";
import { ApiError } from "@/services";
import type { SeatTopUpQuote } from "@/types/api";
import { useEffect, useState } from "react";

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Seats to pre-fill — the shortfall, when opened from a failed invite. */
	defaultAdditional?: number;
	/** Addresses that could not be invited, so the dialog can name them. */
	blockedEmails?: string[];
	cycle?: string;
	/** Seats the pool holds right now; guards against a stale quote. */
	currentSeats: number | null;
	/**
	 * Run after the seats exist. Used to send the invites that were blocked —
	 * buying a seat and then having to retype the address is a pointless
	 * second step when we already know who it was for.
	 */
	onPurchased?: () => void | Promise<void>;
}

/**
 * Buy seats.
 *
 * The whole point of this screen is that the admin sees the exact figure
 * before committing. The quote comes from the server — the same function that
 * writes the invoice — so there is no arithmetic here to drift out of step
 * with what actually gets charged.
 */
export function BuySeatsDialog({
	open,
	onOpenChange,
	defaultAdditional = 1,
	blockedEmails,
	cycle,
	currentSeats,
	onPurchased,
}: Props) {
	const [count, setCount] = useState(defaultAdditional);
	const [done, setDone] = useState<{
		quote: SeatTopUpQuote;
		payment_url: string | null;
	} | null>(null);
	const [error, setError] = useState<string | null>(null);

	// Reopening for a different shortfall must not show the last one's number.
	useEffect(() => {
		if (open) {
			setCount(defaultAdditional);
			setDone(null);
			setError(null);
		}
	}, [open, defaultAdditional]);

	const quoteQ = useSeatQuote(count, open && !done);
	const buy = useBuySeats();
	const quote = quoteQ.data?.quote;
	const per = cycleNoun(cycle);

	async function onConfirm() {
		setError(null);
		try {
			const r = await buy.mutateAsync({
				additional: count,
				expectedCurrentSeats: currentSeats,
			});
			setDone({ quote: r.quote, payment_url: r.invoice.payment_url });
			await onPurchased?.();
		} catch (e) {
			setError(
				e instanceof ApiError
					? e.message
					: "Could not complete the purchase. Nothing has been charged.",
			);
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				{done ? (
					<Purchased
						quote={done.quote}
						paymentUrl={done.payment_url}
						per={per}
						invited={blockedEmails?.length ?? 0}
						onClose={() => onOpenChange(false)}
					/>
				) : (
					<>
						<DialogHeader>
							<DialogTitle>Add seats</DialogTitle>
							<DialogDescription>
								{blockedEmails?.length
									? `Your seats are full, so ${blockedEmails.length === 1 ? "this invite needs" : `these ${blockedEmails.length} invites need`} a new seat. New seats are charged for the rest of this ${per}.`
									: `New seats are charged up front for the rest of this ${per}.`}
							</DialogDescription>
						</DialogHeader>

						{blockedEmails?.length ? (
							<p className="-mt-1 truncate text-caption text-muted-foreground">
								{blockedEmails.join(", ")}
							</p>
						) : null}

						<div className="flex items-center gap-3">
							<label htmlFor="seat-count" className="text-body text-heading">
								Seats
							</label>
							<Input
								id="seat-count"
								type="number"
								min={1}
								className="w-24 tabular"
								value={count}
								onChange={(e) => {
									const n = Number.parseInt(e.target.value, 10);
									setCount(Number.isNaN(n) ? 0 : n);
								}}
							/>
							{currentSeats != null ? (
								<span className="text-caption text-muted-foreground">
									{currentSeats} now → {currentSeats + (count > 0 ? count : 0)}{" "}
									after
								</span>
							) : null}
						</div>

						{count < 1 ? (
							<p className="text-caption text-muted-foreground">
								Enter how many seats to add.
							</p>
						) : quoteQ.isLoading ? (
							<Skeleton className="h-32 w-full" />
						) : quoteQ.isError ? (
							<p className="text-caption text-error">
								{quoteQ.error instanceof ApiError
									? quoteQ.error.message
									: "Could not price these seats."}
							</p>
						) : quote ? (
							<QuoteBreakdown quote={quote} per={per} />
						) : null}

						{error ? <p className="text-caption text-error">{error}</p> : null}

						<DialogFooter>
							<Button variant="ghost" onClick={() => onOpenChange(false)}>
								Cancel
							</Button>
							<Button
								onClick={onConfirm}
								disabled={!quote || buy.isPending || count < 1}
							>
								{buy.isPending
									? "Adding…"
									: quote
										? `Add ${count} ${count === 1 ? "seat" : "seats"} — ${formatMoney(quote.total_cents, quote.currency)}`
										: "Add seats"}
							</Button>
						</DialogFooter>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}

/**
 * Every number an invoice will show, and nothing it won't.
 *
 * Proration is the part people query, so the days are stated explicitly rather
 * than left to explain a total that looks too small.
 */
function QuoteBreakdown({
	quote,
	per,
}: { quote: SeatTopUpQuote; per: string }) {
	const c = quote.currency;
	return (
		<div className="rounded-md border border-divider bg-surface2 p-4">
			<Row
				label={`${quote.additional_seats} × seat, ${quote.days_remaining} of ${quote.period_days} days left`}
				value={`${formatMoney(quote.prorated_unit_cents, c)} each`}
			/>
			<div className="my-2.5 h-px bg-divider" />
			<div className="flex items-baseline justify-between">
				<span className="text-body font-medium text-heading">Due now</span>
				<span className="tabular text-title2 font-semibold text-heading">
					{formatMoney(quote.total_cents, c)}
				</span>
			</div>

			<p className="mt-3 text-caption text-muted-foreground">
				At renewal these seats cost {formatMoney(quote.full_period_cents, c)}{" "}
				per {per} ({formatMoney(quote.unit_price_cents, c)} each).
			</p>

			{/*
			 * Crossing a bracket is good news and worth saying plainly — but only
			 * the new seats get the better rate today. Existing seats are paid to
			 * the end of the period at the old one and move across at renewal.
			 */}
			{quote.crosses_bracket ? (
				<p className="mt-2 text-caption text-orange-text">
					This moves you into the {formatMoney(quote.unit_price_cents, c)} per
					seat bracket, down from{" "}
					{formatMoney(quote.previous_unit_price_cents, c)}. Your existing seats
					move to that rate at renewal.
				</p>
			) : null}
		</div>
	);
}

/**
 * Seats are granted when the invoice is ISSUED, not when it clears — so the
 * confirmation has to say both halves, or an admin waits for a payment to
 * settle before inviting anyone.
 */
function Purchased({
	quote,
	paymentUrl,
	per,
	invited,
	onClose,
}: {
	quote: SeatTopUpQuote;
	paymentUrl: string | null;
	per: string;
	invited: number;
	onClose: () => void;
}) {
	return (
		<>
			<DialogHeader>
				<DialogTitle>
					{quote.additional_seats}{" "}
					{quote.additional_seats === 1 ? "seat" : "seats"} added
				</DialogTitle>
				<DialogDescription>
					{invited > 0
						? `The ${invited === 1 ? "invite that was" : `${invited} invites that were`} waiting ${invited === 1 ? "has" : "have"} been sent. `
						: "You can invite people now — the seats are live. "}
					An invoice for {formatMoney(quote.total_cents, quote.currency)}{" "}
					covering the rest of this {per} has been sent to your billing contact.
				</DialogDescription>
			</DialogHeader>
			<DialogFooter>
				{paymentUrl ? (
					<Button asChild variant="secondary">
						<a href={paymentUrl} target="_blank" rel="noreferrer">
							Pay now
						</a>
					</Button>
				) : null}
				<Button onClick={onClose}>Done</Button>
			</DialogFooter>
		</>
	);
}

function Row({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex items-baseline justify-between gap-4">
			<span className="text-caption text-muted-foreground">{label}</span>
			<span className="tabular text-body text-heading">{value}</span>
		</div>
	);
}
